/**
 * Queue API
 * 
 * Functions for the global scrape queue view.
 */

import {
  classifyDedupeSkip,
  DEDUPE_SKIP_PREFIXES,
  DEDUPE_SKIP_REASONS,
  type DedupeSkipReason,
} from '@repo/adapter-core/crawl-policy';
import { supabase } from '@/integrations/supabase/client';

// ============================================================
// Types
// ============================================================

export interface QueueItemWithRelations {
  id: string;
  job_id: string;
  job_run_id: string | null;
  adapter_id: string;
  suburb_id: string | null;
  url: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  attempts: number;
  last_error: string | null;
  next_retry_at: string | null;
  locked_at: string | null;
  locked_by: string | null;
  scraped_data: Record<string, unknown> | null;
  scraped_at: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  scrape_jobs: { id: string; name: string } | null;
  adapters: { id: string; name: string; slug: string } | null;
  suburbs: { id: string; suburb_name: string; state: string; postcode: string } | null;
}

export interface QueueFilters {
  status?: string;
  jobId?: string;
  adapterId?: string;
  search?: string;
}

export interface QueueFetchOptions {
  filters?: QueueFilters;
  limit?: number;
  offset?: number;
}

export interface QueueStats {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
}

export interface CrawlLogEntry {
  id: string;
  job_id: string;
  job_run_id: string | null;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  // Relations
  scrape_jobs?: { id: string; name: string } | null;
  job_runs?: { id: string; status: string; phase: string | null } | null;
}

// ============================================================
// Queue Operations
// ============================================================

/**
 * Fetch global queue with pagination and filters
 */
export async function fetchGlobalQueue(options: QueueFetchOptions = {}): Promise<{
  data: QueueItemWithRelations[];
  count: number;
}> {
  const { filters = {}, limit = 50, offset = 0 } = options;

  let query = (supabase as any)
    .from('scrape_queue')
    .select(`
      *,
      scrape_jobs!scrape_queue_job_id_fkey(id, name),
      adapters!scrape_queue_adapter_id_fkey(id, name, slug),
      suburbs!scrape_queue_suburb_id_fkey(id, suburb_name, state, postcode)
    `, { count: 'exact' });

  // Apply filters
  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }
  if (filters.jobId) {
    query = query.eq('job_id', filters.jobId);
  }
  if (filters.adapterId) {
    query = query.eq('adapter_id', filters.adapterId);
  }
  if (filters.search) {
    query = query.ilike('url', `%${filters.search}%`);
  }

  // Apply pagination and ordering
  query = query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching global queue:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get global queue stats (across all jobs)
 * Uses individual count queries to avoid Supabase's 1000-row default limit.
 */
/**
 * Fresh counts for a single job run, derived from scrape_queue rows. Use
 * this in UI components that show progress instead of trusting the cached
 * `job_runs.urls_discovered` / `urls_scraped` columns, which only the
 * Supabase cron-worker updates and are stale for Firebase-path runs.
 *
 * We issue 5 parallel head-count queries; total round-trip is ~50ms typically.
 * Callers should debounce or rely on Supabase Realtime subscriptions to
 * scrape_queue to avoid hot polling.
 */
export async function fetchJobRunCounts(jobRunId: string): Promise<QueueStats> {
  const statuses = ['pending', 'in_progress', 'completed', 'failed'] as const;
  const base = (supabase as any).from('scrape_queue');

  const [totalResult, ...statusResults] = await Promise.all([
    base.select('*', { count: 'exact', head: true }).eq('job_run_id', jobRunId),
    ...statuses.map((status) =>
      base.select('*', { count: 'exact', head: true })
        .eq('job_run_id', jobRunId)
        .eq('status', status),
    ),
  ]);

  if (totalResult.error) {
    console.error('Error fetching job run counts:', totalResult.error);
    throw totalResult.error;
  }

  return {
    total: totalResult.count || 0,
    pending: statusResults[0].count || 0,
    in_progress: statusResults[1].count || 0,
    completed: statusResults[2].count || 0,
    failed: statusResults[3].count || 0,
  };
}

/**
 * Batch version: fetch fresh totals for every job_run of a given job in a
 * single round-trip. Returns a Map keyed by job_run_id with total/completed
 * counts. History tab uses this so it doesn't issue N separate count
 * queries (one per row).
 */
export interface JobRunCounts {
  total: number;
  completed: number;
  failed: number;
  outOfRange: number;
  pending: number;
}

/** Run-detail header counts: JobRunCounts plus dedupe skips split by reason. */
export interface JobRunSummary extends JobRunCounts {
  /**
   * 'skipped' rows per scrape_queue_skip_known_url reason: complete (sale already held
   * completely), held-incomplete (held, completeMissing off), incomplete-cooldown (held
   * incompletely, fetched by this adapter < 30 days ago), listing-cooldown (for-sale lane,
   * fetched by this adapter < 30 days ago), live (another run holds the URL).
   */
  skipped: Record<DedupeSkipReason, number>;
}

export interface JobRunDetailQueueRow {
  id: string;
  url: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  is_out_of_range: boolean;
  last_error: string | null;
  scraped_at: string | null;
  attempts: number;
  scraped_data: Record<string, unknown> | null;
  adapter_id: string;
  adapter_slug: string;
  adapter_name: string;
  suburb_id: string;
  suburb_name: string;
  state: string;
  postcode: string;
}

export interface JobRunDetail {
  rows: JobRunDetailQueueRow[];
  counts: JobRunCounts;
}

/**
 * True per-run totals via head-count queries. These are the authoritative
 * "total / scraped / failed / out-of-range / pending" numbers the run-detail
 * page header displays — independent of how many rows the table has
 * actually loaded so far.
 */
export async function fetchJobRunSummary(jobRunId: string): Promise<JobRunSummary> {
  const base = (supabase as any).from('scrape_queue');
  // scrape_queue_skip_known_url writes a reason-coded last_error on each 'skipped' row; count
  // each reason by its prefixes (current and older texts).
  const countSkipped = async (reason: DedupeSkipReason): Promise<number> => {
    const results = await Promise.all(
      DEDUPE_SKIP_PREFIXES[reason].map((prefix) =>
        base
          .select('id', { count: 'exact', head: true })
          .eq('job_run_id', jobRunId)
          .eq('status', 'skipped')
          .like('last_error', `${prefix}%`),
      ),
    );
    return results.reduce((sum: number, res: { count: number | null }) => sum + (res.count ?? 0), 0);
  };
  const [totalRes, completedRes, failedRes, outOfRangeRes, pendingRes, skippedCounts] = await Promise.all([
    base.select('id', { count: 'exact', head: true }).eq('job_run_id', jobRunId),
    base.select('id', { count: 'exact', head: true }).eq('job_run_id', jobRunId).eq('status', 'completed'),
    base.select('id', { count: 'exact', head: true }).eq('job_run_id', jobRunId).eq('status', 'failed'),
    base.select('id', { count: 'exact', head: true }).eq('job_run_id', jobRunId).eq('is_out_of_range', true),
    base.select('id', { count: 'exact', head: true }).eq('job_run_id', jobRunId).in('status', ['pending', 'in_progress', 'processing']),
    Promise.all(DEDUPE_SKIP_REASONS.map(countSkipped)),
  ]);
  if (totalRes.error) {
    console.error('fetchJobRunSummary: total count failed', totalRes.error);
  }
  return {
    total: totalRes.count ?? 0,
    completed: completedRes.count ?? 0,
    failed: failedRes.count ?? 0,
    outOfRange: outOfRangeRes.count ?? 0,
    pending: pendingRes.count ?? 0,
    skipped: Object.fromEntries(
      DEDUPE_SKIP_REASONS.map((reason, i) => [reason, skippedCounts[i]]),
    ) as Record<DedupeSkipReason, number>,
  };
}

export interface FetchJobRunRowsOptions {
  offset?: number;
  limit?: number;
  search?: string;
}

/**
 * Paginated scrape_queue rows for a run. Returns the requested slice plus
 * the *filtered* total count (so the UI can show "showing N of M matches"
 * when the user searches). When no search is set, the count matches
 * `fetchJobRunSummary().total`.
 */
export async function fetchJobRunRows(
  jobRunId: string,
  options: FetchJobRunRowsOptions = {},
): Promise<{ data: JobRunDetailQueueRow[]; count: number }> {
  const { offset = 0, limit = 200, search } = options;
  let query = (supabase as any)
    .from('scrape_queue')
    .select(
      `id, url, status, is_out_of_range, last_error, scraped_at, attempts, scraped_data,
       adapter_id, suburb_id,
       adapters!scrape_queue_adapter_id_fkey(name, slug),
       suburbs!scrape_queue_suburb_id_fkey(suburb_name, state, postcode)`,
      { count: 'exact' },
    )
    .eq('job_run_id', jobRunId);

  // Push URL-only ILIKE down to Postgres. Suburb/adapter token-AND search
  // stays client-side for now — pushing a join filter through PostgREST
  // gets messy and the URL substring usually narrows enough.
  const trimmed = search?.trim();
  if (trimmed) {
    const tokens = trimmed.split(/\s+/).filter(Boolean);
    for (const token of tokens) {
      query = query.ilike('url', `%${token}%`);
    }
  }

  query = query
    .order('scraped_at', { ascending: false, nullsFirst: false })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error || !data) {
    console.error('fetchJobRunRows error', error);
    return { data: [], count: 0 };
  }

  const rows: JobRunDetailQueueRow[] = (data as Array<{
    id: string;
    url: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    is_out_of_range: boolean | null;
    last_error: string | null;
    scraped_at: string | null;
    attempts: number | null;
    scraped_data: Record<string, unknown> | null;
    adapter_id: string;
    suburb_id: string | null;
    adapters: { name: string; slug: string } | null;
    suburbs: { suburb_name: string; state: string; postcode: string } | null;
  }>).map((row) => ({
    id: row.id,
    url: row.url,
    status: row.status,
    is_out_of_range: row.is_out_of_range === true,
    last_error: row.last_error,
    scraped_at: row.scraped_at,
    attempts: row.attempts ?? 0,
    scraped_data: row.scraped_data,
    adapter_id: row.adapter_id,
    adapter_slug: row.adapters?.slug ?? 'unknown',
    adapter_name: row.adapters?.name ?? 'Unknown',
    suburb_id: row.suburb_id ?? '',
    suburb_name: row.suburbs?.suburb_name ?? 'Unknown',
    state: row.suburbs?.state ?? '??',
    postcode: row.suburbs?.postcode ?? '',
  }));

  return { data: rows, count: count ?? 0 };
}

/**
 * @deprecated Use fetchJobRunSummary + fetchJobRunRows. Kept temporarily
 * until the run-detail page migration lands.
 */
export async function fetchJobRunDetail(jobRunId: string): Promise<JobRunDetail> {
  const [summary, rows] = await Promise.all([
    fetchJobRunSummary(jobRunId),
    fetchJobRunRows(jobRunId, { offset: 0, limit: 100000 }),
  ]);
  return { rows: rows.data, counts: summary };
}

/**
 * Per-run queue counts for a job's History tab, including dedupe skips by reason.
 * Grouped in SQL (job_run_queue_breakdown): fetching rows hit PostgREST's 1000-row cap.
 */
export async function fetchJobRunCountsBatch(
  jobId: string,
): Promise<Map<string, JobRunSummary>> {
  const { data, error } = await (supabase as any).rpc('job_run_queue_breakdown', {
    p_job_id: jobId,
  });

  if (error) {
    console.error('Error fetching batch run counts:', error);
    return new Map();
  }

  const map = new Map<string, JobRunSummary>();
  for (const row of (data ?? []) as Array<{
    job_run_id: string;
    status: string;
    is_out_of_range: boolean;
    skip_note: string | null;
    row_count: number | string;
  }>) {
    const n = Number(row.row_count);
    const counts = map.get(row.job_run_id) ?? {
      total: 0,
      completed: 0,
      failed: 0,
      outOfRange: 0,
      pending: 0,
      skipped: Object.fromEntries(DEDUPE_SKIP_REASONS.map((reason) => [reason, 0])) as Record<
        DedupeSkipReason,
        number
      >,
    };
    counts.total += n;
    if (row.status === 'completed') counts.completed += n;
    if (row.status === 'failed') counts.failed += n;
    if (row.is_out_of_range) counts.outOfRange += n;
    if (['pending', 'in_progress', 'processing'].includes(row.status)) counts.pending += n;
    const reason = row.status === 'skipped' ? classifyDedupeSkip(row.skip_note) : null;
    if (reason) counts.skipped[reason] += n;
    map.set(row.job_run_id, counts);
  }
  return map;
}

/**
 * Per-suburb property + sale-date stats for a job's suburbs. Joins
 * properties (the deduped, post-trigger view) to scrape_queue rows of
 * the given job run via URL membership. Returns one row per suburb_name.
 *
 * This is heavier than the queue-counts queries — issue once per dialog
 * open, not on every tab switch.
 */
export interface JobSuburbPropertyStats {
  suburb_name: string;
  properties_count: number;
  min_sale_date: string | null;
  max_sale_date: string | null;
  avg_sale_price: number | null;
}

export async function fetchJobSuburbPropertyStats(
  jobRunId: string,
): Promise<Map<string, JobSuburbPropertyStats>> {
  // First grab every scraped URL for this run, then resolve which
  // properties have any of those URLs in `source_urls`. Done in two
  // round-trips because postgres array-overlap operators aren't
  // expressible through Supabase's PostgREST client cleanly.
  const { data: queueRows, error: qErr } = await (supabase as any)
    .from("scrape_queue")
    .select("url, suburb_id, suburbs(suburb_name)")
    .eq("job_run_id", jobRunId)
    .eq("status", "completed")
    .limit(20000);
  if (qErr) {
    console.error("fetchJobSuburbPropertyStats queue error", qErr);
    return new Map();
  }
  const urls = (queueRows ?? []).map((r: { url: string }) => r.url);
  if (urls.length === 0) return new Map();

  const { data: propRows, error: pErr } = await (supabase as any)
    .from("properties")
    .select("suburb, last_sale_date, last_sale_price, price, source_urls")
    .overlaps("source_urls", urls)
    .limit(20000);
  if (pErr) {
    console.error("fetchJobSuburbPropertyStats props error", pErr);
    return new Map();
  }

  const stats = new Map<string, JobSuburbPropertyStats>();
  for (const p of (propRows ?? []) as Array<{
    suburb: string | null;
    last_sale_date: string | null;
    last_sale_price: number | null;
    price: number | null;
  }>) {
    const key = (p.suburb ?? "").trim();
    if (!key) continue;
    const existing = stats.get(key) ?? {
      suburb_name: key,
      properties_count: 0,
      min_sale_date: null as string | null,
      max_sale_date: null as string | null,
      avg_sale_price: null as number | null,
    };
    existing.properties_count += 1;
    if (p.last_sale_date) {
      if (!existing.min_sale_date || p.last_sale_date < existing.min_sale_date) {
        existing.min_sale_date = p.last_sale_date;
      }
      if (!existing.max_sale_date || p.last_sale_date > existing.max_sale_date) {
        existing.max_sale_date = p.last_sale_date;
      }
    }
    const sale = p.last_sale_price ?? p.price ?? null;
    if (sale !== null) {
      // Welford-ish running average via existing count
      const prevCount = existing.properties_count - 1;
      if (existing.avg_sale_price === null) existing.avg_sale_price = sale;
      else existing.avg_sale_price = (existing.avg_sale_price * prevCount + sale) / existing.properties_count;
    }
    stats.set(key, existing);
  }
  return stats;
}

export async function getGlobalQueueStats(): Promise<QueueStats> {
  const statuses = ['pending', 'in_progress', 'completed', 'failed'] as const;
  
  const [totalResult, ...statusResults] = await Promise.all([
    (supabase as any).from('scrape_queue').select('*', { count: 'exact', head: true }),
    ...statuses.map(status => 
      (supabase as any).from('scrape_queue').select('*', { count: 'exact', head: true }).eq('status', status)
    ),
  ]);

  if (totalResult.error) {
    console.error('Error fetching global queue stats:', totalResult.error);
    throw totalResult.error;
  }

  return {
    total: totalResult.count || 0,
    pending: statusResults[0].count || 0,
    in_progress: statusResults[1].count || 0,
    completed: statusResults[2].count || 0,
    failed: statusResults[3].count || 0,
  };
}

// ============================================================
// Crawl Log Operations
// ============================================================

/**
 * Fetch crawl logs with optional filtering. The new filters (levels[],
 * kinds[], adapter, suburb, search) read the structured fields the
 * Firebase workers now write via `logDiscoveryPage` / `logScrapeAttempt`
 * etc. — `metadata.kind` is the broad category (discovery/scrape/seat/
 * provider/trigger/checkpoint).
 */
export async function fetchCrawlLogs(options: {
  jobRunId?: string;
  jobId?: string;
  level?: string;
  levels?: string[];
  kinds?: string[];
  adapter?: string;
  suburb?: string;
  search?: string;
  sinceISO?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ data: CrawlLogEntry[]; count: number }> {
  const {
    jobRunId,
    jobId,
    level,
    levels,
    kinds,
    adapter,
    suburb,
    search,
    sinceISO,
    limit = 100,
    offset = 0,
  } = options;

  let query = (supabase as any)
    .from('crawl_logs')
    .select(`
      *,
      scrape_jobs!crawl_logs_job_id_fkey(id, name)
    `, { count: 'exact' });

  if (jobRunId) query = query.eq('job_run_id', jobRunId);
  if (jobId) query = query.eq('job_id', jobId);
  if (level && level !== 'all') query = query.eq('level', level);
  if (levels && levels.length > 0) query = query.in('level', levels);
  if (kinds && kinds.length > 0) query = query.in('metadata->>kind', kinds);
  if (adapter) query = query.eq('metadata->>adapter', adapter);
  if (suburb) query = query.eq('metadata->>suburb', suburb);
  if (search) query = query.ilike('message', `%${search}%`);
  if (sinceISO) query = query.gt('created_at', sinceISO);

  query = query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching crawl logs:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Fetch audit notifications (warn/error logs) with pagination.
 */
export async function fetchAuditNotifications(options: {
  limit?: number;
  offset?: number;
} = {}): Promise<{ data: CrawlLogEntry[]; count: number }> {
  const { limit = 20, offset = 0 } = options;

  const { data, error, count } = await (supabase as any)
    .from('crawl_logs')
    .select(`
      *,
      scrape_jobs!crawl_logs_job_id_fkey(id, name),
      job_runs!crawl_logs_job_run_id_fkey(id, status, phase)
    `, { count: 'exact' })
    .in('level', ['warn', 'error'])
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching audit notifications:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

// ============================================================
// Discovery Tasks
// ============================================================

export interface DiscoveryTask {
  id: string;
  job_id: string;
  job_run_id: string;
  adapter_id: string;
  suburb_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  search_url: string | null;
  current_page: number;
  urls_found: number;
  last_page_had_results: boolean;
  consecutive_empty_pages: number;
  locked_by: string | null;
  locked_at: string | null;
  error_message: string | null;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Relations
  suburbs?: { suburb_name: string; state: string; postcode: string } | null;
  adapters?: { name: string; slug: string } | null;
}

/**
 * Fetch discovery tasks for a specific job run or all active
 */
export async function fetchDiscoveryTasks(jobRunId?: string): Promise<DiscoveryTask[]> {
  let query = (supabase as any)
    .from('discovery_tasks')
    .select(`
      *,
      suburbs!discovery_tasks_suburb_id_fkey(suburb_name, state, postcode),
      adapters!discovery_tasks_adapter_id_fkey(name, slug)
    `)
    .order('created_at', { ascending: true });

  if (jobRunId) {
    query = query.eq('job_run_id', jobRunId);
  } else {
    // Get tasks from any running job
    query = query.in('status', ['pending', 'in_progress']);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error fetching discovery tasks:', error);
    return [];
  }
  return data || [];
}

/**
 * Fetch discovery task stats for active runs
 */
export async function fetchDiscoveryStats(): Promise<{
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  failed: number;
}> {
  const { data, error } = await (supabase as any)
    .from('discovery_tasks')
    .select(`
      status,
      job_runs!inner(status)
    `)
    .eq('job_runs.status', 'running')
    .in('status', ['pending', 'in_progress', 'completed', 'failed']);

  if (error || !data) return { total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 };

  return {
    total: data.length,
    pending: data.filter((d: { status: string }) => d.status === 'pending').length,
    inProgress: data.filter((d: { status: string }) => d.status === 'in_progress').length,
    completed: data.filter((d: { status: string }) => d.status === 'completed').length,
    failed: data.filter((d: { status: string }) => d.status === 'failed').length,
  };
}

/**
 * Fetch active job runs (for live monitor)
 */
export async function fetchActiveJobRuns(): Promise<Array<{
  id: string;
  job_id: string;
  status: string;
  phase: string | null;
  started_at: string;
  urls_discovered: number | null;
  urls_scraped: number | null;
  records_scraped: number | null;
  metadata: Record<string, unknown> | null;
  last_activity_at: string | null;
  scrape_jobs: { id: string; name: string } | null;
}>> {
  const { data, error } = await (supabase as any)
    .from('job_runs')
    .select(`
      id, job_id, status, phase, started_at, urls_discovered, urls_scraped, 
      records_scraped, metadata, last_activity_at,
      scrape_jobs!job_runs_job_id_fkey(id, name)
    `)
    .in('status', ['running'])
    .order('started_at', { ascending: false });

  if (error) {
    console.error('Error fetching active job runs:', error);
    throw error;
  }

  return data || [];
}
